import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Truck, Accessibility, Activity, Tag, Plus, Pencil, Trash2, Check, X, GripVertical, Eye, EyeOff } from "lucide-react";

const OptionList = ({ type, title, icon: Icon, canEdit }) => {
  const [items, setItems] = useState(null);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [dragId, setDragId] = useState(null);

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) return setDragId(null);
    const from = items.findIndex((i) => i.id === dragId);
    const to = items.findIndex((i) => i.id === targetId);
    const next = [...items];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setItems(next); setDragId(null);
    try {
      await api.put("/options/reorder", { ids: next.map((i) => i.id) }, { params: { type } });
      toast.success("Ordine aggiornato");
    } catch (err) { toast.error("Ordine non salvato", { description: formatApiErrorDetail(err.response?.data?.detail) }); load(); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/options", { params: { type } });
    setItems(data);
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/options", { nome: newName.trim() }, { params: { type } });
      setItems((p) => [...(p || []), data]); setNewName("");
      toast.success(`"${data.nome}" aggiunto a ${title}`);
    } catch (err) { toast.error("Non aggiunto", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const save = async (id) => {
    try {
      const { data } = await api.put(`/options/${id}`, { nome: editName.trim() });
      setItems((p) => p.map((i) => (i.id === id ? data : i))); setEditId(null);
      toast.success("Voce aggiornata (richieste esistenti allineate)");
    } catch (err) { toast.error("Non aggiornato", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Eliminare "${item.nome}"? Le corse storiche manterranno il valore ma la voce non sarà più selezionabile. Per nasconderla soltanto, usa "Disattiva".`)) return;
    try {
      await api.delete(`/options/${item.id}`);
      setItems((p) => p.filter((i) => i.id !== item.id));
      toast.success("Voce eliminata");
    } catch (err) { toast.error("Non eliminato", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  const toggle = async (item) => {
    try {
      const { data } = await api.patch(`/options/${item.id}/toggle`);
      setItems((p) => p.map((i) => (i.id === item.id ? data : i)));
      toast.success(data.active ? `"${data.nome}" riattivata nei menu` : `"${data.nome}" nascosta dai menu`);
    } catch (err) { toast.error("Operazione non riuscita", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  return (
    <Card className="border-slate-200 overflow-hidden" data-testid={`options-${type}`}>
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
        <Icon className="h-5 w-5 text-primary" />
        <h2 className="font-semibold text-slate-800">{title}</h2>
        <span className="ml-auto text-xs text-slate-400">{items ? `${items.length} voci` : ""}</span>
      </div>
      {items === null ? (
        <div className="p-5 space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-9 w-full" />)}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((it) => (
            <li key={it.id} data-testid={`option-row-${it.id}`}
              draggable={canEdit && editId === null}
              onDragStart={() => setDragId(it.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(it.id)}
              onDragEnd={() => setDragId(null)}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm ${dragId === it.id ? "opacity-40" : ""} ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}>
              {canEdit && <GripVertical data-testid={`option-drag-${it.id}`} className="h-4 w-4 text-slate-300 shrink-0" />}
              {editId === it.id ? (
                <>
                  <Input data-testid={`option-edit-input-${it.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter") save(it.id); if (e.key === "Escape") setEditId(null); }} />
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" data-testid={`option-save-${it.id}`} onClick={() => save(it.id)}><Check className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditId(null)}><X className="h-4 w-4" /></Button>
                </>
              ) : (
                <>
                  <span className={`flex-1 ${it.active === false ? "text-slate-400 line-through" : "text-slate-800"}`}>{it.nome}</span>
                  {it.active === false && <span data-testid={`option-inactive-${it.id}`} className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Disattivata</span>}
                  {it.created_by && it.created_by !== "sistema" && <span className="text-[10px] text-slate-400">agg. da {it.created_by}</span>}
                  {canEdit && (
                    <>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-primary" title={it.active === false ? "Riattiva" : "Disattiva (nascondi dai menu)"} data-testid={`option-toggle-${it.id}`} onClick={() => toggle(it)}>
                        {it.active === false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-primary" data-testid={`option-edit-${it.id}`} onClick={() => { setEditId(it.id); setEditName(it.nome); }}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid={`option-delete-${it.id}`} onClick={() => remove(it)}><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="flex gap-2 p-4 border-t border-slate-200 bg-slate-50/60">
        <Input data-testid={`option-new-${type}`} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={`Nuova voce ${title.toLowerCase()}...`} className="h-9" />
        <Button type="submit" size="sm" className="gap-1 h-9" data-testid={`option-add-${type}`} disabled={!newName.trim()}><Plus className="h-4 w-4" /> Aggiungi</Button>
      </form>
    </Card>
  );
};

export default function OptionsPage({ only }) {
  const { user } = useAuth();
  const canEdit = user?.staff_role !== "operatore";
  const isAttivita = only === "attivita";
  const isCausale = only === "causale";
  const title = isAttivita ? "Attività" : isCausale ? "Causali" : "Mezzi e Mobilizzazione";
  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in pb-10">
        <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1">{title}</h1>
        <p className="text-sm text-slate-500 mb-6">
          Voci proposte nel form di prenotazione. {canEdit ? "Puoi aggiungere, rinominare, eliminare e trascinare le voci per ordinarle." : "Come Operatore puoi solo aggiungere nuove voci."}
        </p>
        {isAttivita ? (
          <div className="max-w-2xl"><OptionList type="attivita" title="Attività" icon={Activity} canEdit={canEdit} /></div>
        ) : isCausale ? (
          <div className="max-w-2xl"><OptionList type="causale" title="Causale" icon={Tag} canEdit={canEdit} /></div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <OptionList type="mezzo" title="Tipologia Mezzo" icon={Truck} canEdit={canEdit} />
            <OptionList type="mobilizzazione" title="Mobilizzazione" icon={Accessibility} canEdit={canEdit} />
          </div>
        )}
      </div>
    </AppLayout>
  );
}
